// Shared Zod schemas for the bulk-add analysis job API — the contract between the
// frontend and the parsing backend (and the MSW mock of it). Validated on both
// sides, so the wire shape can never drift.
import { z } from 'zod';
import { noyauCodeSchema, wordTagSchema } from './noyau.js';

/** An existing DB word a candidate collides with (batch-vs-DB conflict). */
export const dbMatchSchema = z.object({
  id: z.string(),
  title: z.string(),
  tags: z.array(wordTagSchema),
});
export type DbMatch = z.infer<typeof dbMatchSchema>;

/** One analyzed candidate word: a title, pre-filled tags, and an optional DB hit. */
export const analyzedWordSchema = z.object({
  title: z.string(),
  tags: z.array(wordTagSchema),
  dbMatch: dbMatchSchema.nullable(),
});
export type AnalyzedWord = z.infer<typeof analyzedWordSchema>;

/** Body of `POST /api/bulk/analyze`: the pasted text + the chosen batch noyau. */
export const analyzeRequestSchema = z.object({
  text: z.string(),
  noyau: noyauCodeSchema.nullable(),
});
export type AnalyzeRequest = z.infer<typeof analyzeRequestSchema>;

/** Response of `POST /api/bulk/analyze` — the id to poll the job by. */
export const analyzeResponseSchema = z.object({
  jobId: z.string(),
});

/**
 * Response of `GET /api/bulk/jobs/:id`. `words` is present only once `status` is
 * `done`; while `processing` it is null and `processed`/`total` drive the bar.
 */
export const bulkJobSchema = z.object({
  status: z.enum(['processing', 'done', 'error']),
  processed: z.number(),
  total: z.number(),
  words: z.array(analyzedWordSchema).nullable(),
});
export type BulkJob = z.infer<typeof bulkJobSchema>;

// ── Draft persistence ─────────────────────────────────────────────────────────
// The full review-session shape, persisted server-side so a draft survives a
// device switch. Shared because the backend validates it on write and the
// frontend validates it on hydrate (both sides parse against this one schema).
// The frontend's lib/bulk-add/types.ts re-exports these.

/** Lifecycle of a review session: input → analyzing → ready → reviewing, or error. */
export const bulkSessionStatusSchema = z.enum([
  'input',
  'analyzing',
  'ready',
  'reviewing',
  'error',
]);
export type BulkSessionStatus = z.infer<typeof bulkSessionStatusSchema>;

/**
 * What a candidate word does at submit time: `create` a new word, or `mergeInto`
 * an existing DB word (set when a batch-vs-DB conflict is resolved with Fusionner).
 */
export const bulkWordOpSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('create') }),
  z.object({ kind: z.literal('mergeInto'), targetId: z.string(), targetTitle: z.string() }),
]);
export type BulkWordOp = z.infer<typeof bulkWordOpSchema>;

/**
 * One candidate word in a review session. Title is read-only; `tags` is the only
 * user-editable field. `excluded` is the reversible cross-out. `noyauWeight`
 * carries the batch-noyau pondération (1 normally, 2 after a Fusionner merge).
 */
export const bulkWordSchema = z.object({
  id: z.string(),
  title: z.string(),
  tags: z.array(wordTagSchema),
  excluded: z.boolean(),
  noyauWeight: z.number(),
  op: bulkWordOpSchema,
  dbMatch: dbMatchSchema.nullable(),
  failed: z.boolean(),
});
export type BulkWord = z.infer<typeof bulkWordSchema>;

/**
 * A whole review session — the persisted draft. `noyau` is the batch noyau (null
 * = indéterminé). `text` is the original pasted input (kept so a dead analysis
 * job can be re-run and the DB match re-checked). `resolvedKeys` records conflict
 * pairs settled with "Garder les deux". `updatedAt` is stamped server-side on
 * every write (authoritative last-modified for the entry cards).
 */
export const bulkSessionSchema = z.object({
  id: z.string(),
  name: z.string(),
  noyau: noyauCodeSchema.nullable(),
  status: bulkSessionStatusSchema,
  jobId: z.string().nullable(),
  text: z.string(),
  processed: z.number(),
  total: z.number(),
  words: z.array(bulkWordSchema),
  resolvedKeys: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BulkSession = z.infer<typeof bulkSessionSchema>;

/** Response of `GET /api/bulk/drafts` — the caller's full sessions. */
export const draftListSchema = z.object({ drafts: z.array(bulkSessionSchema) });
export type DraftList = z.infer<typeof draftListSchema>;
